'use strict';

// external imports
import React, {
    useMemo
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {keys} from 'ts-transformer-keys';

// prime react imports
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

// shared imports
import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';

// frontend imports
import {
    getItemTypeDisplayName
} from '~/frontend/src/utilities/renderHelpers';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import {chartUtilities} from '~/frontend/src/components/base/ChartDesigner/utilities';


export type DefaultProps = {};
export const defaultProps : Required<DefaultProps> = {};

type Props = {
    dataContext : string;

    chartId : number;
    chartKey : string;
    chartName : string;

    style? : CssStyle;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = DefaultProps & Props;


/**
 * Headerbar
 */
const Headerbar = ( props : AllProps ) => {
    const {
              dataContext,
              chartId,
              chartKey,
              chartName,
              style
          }                      = props,
          user                   = useSelector( ( state : StoreState ) => state.session?.user ),
          language               = user
              ? user.language
              : undefined,
          chart               = global.data[ dataContext ]?.items[ chartId ],
          query                  = useMemo(
              () => {
                  return chart ? chartUtilities.getQuery( dataContext, chart.id ) : undefined;
              },
              [
                  dataContext,
                  chart
              ]
          ),
          dataStructure          = useMemo(
              () => {
                  return query?.dataStructure;
              },
              [
                  query
              ]
          ),
          rootDataObjectSchema   = useMemo(
              () => {
                  return ( dataStructure?.item.objectSchema ?? 'DATA' ).toUpperCase() as Schema;
              },
              [
                  dataStructure
              ]
          ),
          rootDataObjectItemType = useMemo(
              () => {
                  return dataStructure?.item.objectType;
              },
              [
                  dataStructure
              ]
          ),
          itemType               = useMemo(
              () => {
                  if ( rootDataObjectSchema && rootDataObjectItemType ) {
                      return metaModel[ rootDataObjectSchema ].getItemType( rootDataObjectItemType );
                  }
                  else {
                      return undefined;
                  }
              },
              [
                  dataStructure
              ]
          );

    return useMemo(
        () => {
            const layoutComponents : ToolbarLayoutComponents = {
                left : [],
                right: []
            };

            // left
            layoutComponents.left.push( {
                type  : 'genericNode',
                params: <label
                    key="title"
                    className="yn-label yn-label-value"
                >
                    {__( 'YN.base.frontend.chartDesigner.header.title' )}
                </label>
            } );

            if ( layoutComponents.left.length ) {
                layoutComponents.left.push( {
                    type  : 'genericNode',
                    params: <i key={`separator_${layoutComponents.left.length}`} className="p-toolbar-separator ml-4 mr-4"/>
                } );
            }

            layoutComponents.left.push( {
                type  : 'genericNode',
                params: <label
                    key="chartItemLabel"
                    className="yn-label yn-label-key mr-2"
                >
                    {__( 'YN.base.frontend.chartDesigner.header.chart.label' )}
                </label>
            } );
            layoutComponents.left.push( {
                type  : 'genericNode',
                params: <label
                    key="chartItem"
                    className="yn-label yn-label-value"
                >
                    {metaModel[ 'CFG' ].getItemType( 'Chart' )?.displayAttribute === 'key' ? chartKey : chartName}
                </label>
            } );

            if ( itemType ) {
                if ( layoutComponents.left.length ) {
                    layoutComponents.left.push( {
                        type  : 'genericNode',
                        params: <i key={`separator_${layoutComponents.left.length}`} className="p-toolbar-separator ml-4 mr-4"/>
                    } );
                }

                layoutComponents.left.push( {
                    type  : 'genericNode',
                    params: <label
                        key="itemType"
                        className="yn-label yn-label-key mr-2"
                    >
                        {__( 'YN.base.frontend.chartDesigner.header.itemType.label' )}
                    </label>
                } );
                layoutComponents.left.push( {
                    type  : 'genericNode',
                    params: <label
                        key="rootDataObjectItemType"
                        className="yn-label yn-label-value"
                    >
                        {getItemTypeDisplayName( itemType.id, rootDataObjectSchema )}
                    </label>
                } );
            }
            else if ( query?.type ) {
                if ( layoutComponents.left.length ) {
                    layoutComponents.left.push( {
                        type  : 'genericNode',
                        params: <i key={`separator_${layoutComponents.left.length}`} className="p-toolbar-separator ml-4 mr-4"/>
                    } );
                }

                layoutComponents.left.push( {
                    type  : 'genericNode',
                    params: <label
                        key="query"
                        className="yn-label yn-label-key mr-2"
                    >
                        {__( 'YN.base.frontend.chartDesigner.header.query.label' )}
                    </label>
                } );
                layoutComponents.left.push( {
                    type  : 'genericNode',
                    params: <label
                        key="queryType"
                        className="yn-label yn-label-value"
                    >
                        {__( `YN.base.frontend.chartDesigner.query.${query.type.toLowerCase()}.label` )}
                    </label>
                } );
            }

            const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

            return <PrimeToolbar
                start={toolbarComponents.left}
                end={toolbarComponents.right}
                className="yn-panel-headerbar yn-toolbar-horizontal-separator-bottom"
                style={{
                    zIndex: 6,
                    ...style
                }}
            />;
        },
        [
            itemType,
            language,
            chartKey,
            chartName,
            query,
            rootDataObjectSchema
        ]
    );
};

Headerbar.defaultProps = defaultProps;
Headerbar.displayName = 'ChartDesigner-Headerbar';

export default Headerbar;
